import {
  TARGET_ACTION_FAMILIES,
  TARGET_BID_METHODS,
  TARGET_BUDGET_METHODS,
  parseTargetAction,
  serializeTargetAction,
  type BuilderConditionGroup,
  type TargetActionFamily,
  type TargetActionMethod,
} from '@/components/automation/automationTypes';

function defaultMethod(family: TargetActionFamily): TargetActionMethod {
  if (family === 'bid_change') return 'decrease_pct';
  if (family === 'parent_campaign_budget') return 'set_fixed';
  return 'decrease_pct';
}

export function TargetActionFields({
  action,
  onChange,
}: {
  action: BuilderConditionGroup['action'];
  onChange: (next: BuilderConditionGroup['action']) => void;
}) {
  const parsed = parseTargetAction(action);
  const methods = parsed.family === 'parent_campaign_budget' ? TARGET_BUDGET_METHODS : TARGET_BID_METHODS;
  const showNumeric =
    (parsed.family === 'bid_change' && parsed.method !== 'set_suggested') ||
    parsed.family === 'parent_campaign_budget';
  const showStatus = parsed.family === 'status_change' || parsed.family === 'parent_campaign_status';

  const update = (patch: Partial<typeof parsed>) => {
    onChange(serializeTargetAction({ ...parsed, ...patch }));
  };

  return (
    <div className="space-y-3">
      <div className="text-sm font-medium text-foreground">Then take this action</div>
      <div className="grid gap-2 sm:grid-cols-[minmax(10rem,14rem)_minmax(10rem,14rem)_minmax(6rem,8rem)_minmax(5rem,7rem)]">
        <label className="block text-sm">
          <span className="mb-1 block text-foreground-muted">
            Action Type <span className="text-red-500">*</span>
          </span>
          <select
            className="w-full rounded border border-border px-2 py-1.5 text-sm"
            value={parsed.family}
            aria-label="Action type"
            onChange={(e) => {
              const family = e.target.value as TargetActionFamily;
              update({
                family,
                method: defaultMethod(family),
                status: 'ENABLED',
                value: family === 'bid_change' || family === 'parent_campaign_budget' ? parsed.value || 1 : 0,
              });
            }}
          >
            {TARGET_ACTION_FAMILIES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        {showNumeric ? (
          <>
            <label className="block text-sm">
              <span className="mb-1 block text-foreground-muted">Method</span>
              <select
                className="w-full rounded border border-border px-2 py-1.5 text-sm"
                value={parsed.method}
                aria-label="Action method"
                onChange={(e) => update({ method: e.target.value as TargetActionMethod })}
              >
                {methods.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-foreground-muted">Type</span>
              <select
                className="w-full rounded border border-border px-2 py-1.5 text-sm"
                value="fixed"
                aria-label="Value type"
                onChange={() => undefined}
              >
                <option value="fixed">Fixed</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-foreground-muted">Value</span>
              <input
                type="number"
                className="w-full rounded border border-border px-2 py-1.5 text-sm"
                value={parsed.value}
                aria-label="Action value"
                onChange={(e) => update({ value: Number(e.target.value) })}
              />
            </label>
          </>
        ) : null}
        {parsed.family === 'bid_change' && parsed.method === 'set_suggested' ? (
          <label className="block text-sm">
            <span className="mb-1 block text-foreground-muted">Method</span>
            <select
              className="w-full rounded border border-border px-2 py-1.5 text-sm"
              value={parsed.method}
              aria-label="Action method"
              onChange={(e) => update({ method: e.target.value as TargetActionMethod })}
            >
              {TARGET_BID_METHODS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {showStatus ? (
          <label className="block text-sm">
            <span className="mb-1 block text-foreground-muted">Status</span>
            <select
              className="w-full rounded border border-border px-2 py-1.5 text-sm"
              value={parsed.status}
              aria-label="Status"
              onChange={(e) => update({ status: e.target.value === 'PAUSED' ? 'PAUSED' : 'ENABLED' })}
            >
              <option value="ENABLED">Enabled</option>
              <option value="PAUSED">Paused</option>
            </select>
          </label>
        ) : null}
      </div>
      <p className="text-xs text-foreground-muted">
        If trigger conditions are met, action will get executed automatically. You can track alerts
        in executed automation alerts tab.
      </p>
    </div>
  );
}
